import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface PlatformStats {
    totalUsers: number;
    verifiedUsers: number;
    totalGigs: number;
    successRate: number;
    averageTrustRating: number;
    countries: number;
}

export interface UserStats {
    // For workers
    totalGigsApplied?: number;
    totalGigsCompleted?: number;
    totalEarnings?: number;
    averageRating?: number;

    // For employers
    totalGigsPosted?: number;
    totalSpent?: number;
}

export interface DashboardStats {
    totalGigs: number;
    activeApplications: number;
    earnings: number;
    rating: number;
    completedJobs: number;
    profileViews: number;
}

export interface RecentActivity {
    id: string;
    type: 'application' | 'message' | 'gig_posted' | 'gig_completed' | 'rating_received';
    title: string;
    description: string;
    date: string;
    status?: 'pending' | 'accepted' | 'completed' | 'rejected' | 'withdrawn';
}

export interface DashboardData {
    stats: DashboardStats;
    recentActivity: RecentActivity[];
    user: {
        id: string;
        role: string;
        profile: any;
        createdAt: string;
    };
}

export const statsApi = createApi({
    reducerPath: 'statsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/v1/stats`,
        credentials: 'include',
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('token');
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['PlatformStats', 'UserStats', 'DashboardStats'],
    endpoints: (builder) => ({
        getPlatformStats: builder.query<
            { success: boolean; data: PlatformStats; message: string },
            void
        >({
            query: () => '/platform',
            providesTags: ['PlatformStats'],
            keepUnusedDataFor: 300,
        }),
        getUserStats: builder.query<
            { success: boolean; data: UserStats; message: string },
            string
        >({
            query: (userId) => `/user/${userId}`,
            providesTags: ['UserStats'],
            keepUnusedDataFor: 60,
        }),
        getDashboardData: builder.query<
            { success: boolean; data: DashboardData; message: string },
            void
        >({
            query: () => '/dashboard',
            providesTags: ['DashboardStats'],
            keepUnusedDataFor: 60,
        }),
    }),
});

export const {
    useGetPlatformStatsQuery,
    useGetUserStatsQuery,
    useGetDashboardDataQuery,
} = statsApi;